import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { descarteAPI } from '../services/api';

const REC_STYLES = {
  descarte: 'bg-red-100 text-red-800',
  revisar: 'bg-amber-100 text-amber-800',
  monitorear: 'bg-blue-100 text-blue-800',
};

const DescartePage = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState({});

  useEffect(() => {
    if (user?.establecimiento_id) loadData();
  }, [user]);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await descarteAPI.candidatos(user.establecimiento_id);
      setData(res.data);
    } catch {}
    setLoading(false);
  };

  const handleAction = async (animalId, accion) => {
    setActionLoading(p => ({ ...p, [animalId]: true }));
    try {
      await descarteAPI.marcar(animalId, accion);
      await loadData();
    } catch {}
    setActionLoading(p => ({ ...p, [animalId]: false }));
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="inline-block w-8 h-8 border-4 border-red-600 border-t-transparent rounded-full animate-spin mb-3" />
          <p className="text-gray-500">Analizando rodeo...</p>
        </div>
      </div>
    );
  }

  const res = data?.resumen || {};
  const cands = data?.candidatos || [];

  return (
    <div className="p-4 sm:p-6 space-y-6" data-testid="descarte-page">
      <div>
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Sistema de Descarte</h2>
        <p className="text-sm text-gray-500 mt-1">Analisis multi-criterio automatico basado en PDF tecnico y zootecnia</p>
      </div>

      {/* Resumen */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <SCard label="Total Rodeo" value={res.total_animales || 0} border="border-gray-300" />
        <SCard label="Candidatos" value={res.total_candidatos || 0} border="border-gray-400" />
        <SCard label="Descarte" value={res.descarte_inmediato || 0} border="border-red-500" color="text-red-700" />
        <SCard label="Revisar" value={res.revisar || 0} border="border-amber-500" color="text-amber-700" />
        <SCard label="Monitorear" value={res.monitorear || 0} border="border-blue-500" color="text-blue-700" />
      </div>

      {/* Info box */}
      <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 text-sm text-gray-600">
        <strong>Criterios de evaluacion:</strong> EP baja (&lt;42% adultas / &lt;40% primiparas) | IEP &gt;365 dias | Peso &lt;250 kg a 12m | Sin pariciones | GMD negativa | Frame fuera de rango.
        El puntaje combina todos los factores (0-100). Mayor puntaje = mayor prioridad de descarte.
      </div>

      {/* Tabla */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden" data-testid="descarte-table">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                {['Puntaje', 'VID', 'Sexo', 'Raza', 'Edad', 'Peso', 'Recom.', 'Razones', 'Acciones'].map(h => (
                  <th key={h} className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {cands.length === 0 && (
                <tr><td colSpan={9} className="text-center py-12 text-gray-400">Sin candidatos a descarte. El rodeo esta en buenas condiciones.</td></tr>
              )}
              {cands.map((c, i) => (
                <tr key={c.animal_id} className={`border-b border-gray-100 hover:bg-gray-50 ${i % 2 !== 0 ? 'bg-gray-50/50' : ''}`} data-testid={`descarte-row-${c.VID}`}>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-2">
                      <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm"
                        style={{ backgroundColor: c.puntaje_descarte >= 50 ? '#dc2626' : c.puntaje_descarte >= 25 ? '#d97706' : '#2563eb' }}>
                        {c.puntaje_descarte}
                      </div>
                    </div>
                  </td>
                  <td className="py-3 px-4 font-semibold">{c.VID}</td>
                  <td className="py-3 px-4 capitalize">{c.sexo === 'macho' ? 'M' : 'H'}</td>
                  <td className="py-3 px-4 text-gray-600">{c.raza || '—'}</td>
                  <td className="py-3 px-4">{c.edad_meses}m</td>
                  <td className="py-3 px-4">{c.peso ? `${c.peso.toFixed(0)} kg` : '—'}</td>
                  <td className="py-3 px-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold capitalize ${REC_STYLES[c.recomendacion] || 'bg-gray-100'}`}>
                      {c.recomendacion}
                    </span>
                  </td>
                  <td className="py-3 px-4 max-w-xs">
                    <div className="space-y-1">
                      {c.razones.map((r, j) => (
                        <p key={j} className="text-xs text-gray-600">- {r}</p>
                      ))}
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex gap-1">
                      <button
                        onClick={() => handleAction(c.animal_id, 'descarte')}
                        disabled={actionLoading[c.animal_id]}
                        className="px-2 py-1 bg-red-600 text-white rounded text-xs font-semibold hover:bg-red-700 disabled:opacity-50 transition"
                        data-testid={`btn-descarte-${c.VID}`}
                      >
                        Descartar
                      </button>
                      <button
                        onClick={() => handleAction(c.animal_id, 'mantener')}
                        disabled={actionLoading[c.animal_id]}
                        className="px-2 py-1 bg-green-600 text-white rounded text-xs font-semibold hover:bg-green-700 disabled:opacity-50 transition"
                        data-testid={`btn-mantener-${c.VID}`}
                      >
                        Mantener
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const SCard = ({ label, value, border, color = 'text-gray-900' }) => (
  <div className={`bg-white rounded-xl shadow-sm p-4 border-l-4 ${border}`}>
    <p className="text-xs font-medium text-gray-500 uppercase">{label}</p>
    <p className={`text-2xl font-bold mt-1 ${color}`}>{value}</p>
  </div>
);

export default DescartePage;
